import { moodLabel, signedFigure, type MoodCheckin } from '@adhd/shared';
import { Link } from 'react-router-dom';
import { bandOf } from '../lib/band.js';
import type { OwnLifeEvent } from '../lib/api.js';
import { useState } from 'react';
import { StartThread } from './StartThread.js';
import { ThreadChips } from './ThreadChips.js';

/**
 * The conditions block: the top of Today since release 1 of 2.0.
 *
 * The latest check-in's band tints the block and its word is the biggest thing
 * on the screen; the figure is small and mono underneath, with when it was said
 * and how many times today. Nothing here is a score — a word for how the day
 * is right now, in the colour the ten-day strip and the Days grid will use for
 * the same band, so today and the record are visibly the same thing.
 *
 * NEVER --text-faint ON A TINT. The faint tier measures 2.7:1 on the green
 * tint; the two tiers used here hold 9.7:1 and 5.2:1 at worst
 * (docs/pitch-2026-10-12/crafted/pitch.html, and the skin's own comment).
 *
 * Under the word: what is running. A life event is a thread in the 2.0 sense —
 * a name, a date, a count he keeps himself — and the strip shows each running
 * one with its day count. The app never names one for him; an unnamed event
 * reads "Something big", as it does everywhere else.
 */
export function Conditions({
  last,
  checkins,
  describe,
  events,
  suffix,
  today,
  peopleCount = 0,
  onThreadStarted,
}: {
  /** The day's most recent check-in, or null. /mood/today is newest-first. */
  last: MoodCheckin | null;
  checkins: number;
  /** The sentence Today's header used to open with, kept for a day with no check-in. */
  describe: string;
  /** Running life events, already filtered to those covering today. */
  events: OwnLifeEvent[];
  /** Today's day key, for the counts. */
  /** The fixes pass: the time of day, said once in the date line in place of a face switch. */
  suffix?: string;
  today: string;
  /** How many people are in the record, for the People chip. 0 hides it. */
  peopleCount?: number;
  /** Called with the new thread once one is started here. */
  onThreadStarted?: (t: OwnLifeEvent) => void;
}): JSX.Element {
  const band = last ? bandOf(last.mood) : null;
  const [starting, setStarting] = useState(false);
  // The date line tells the truth about the clock (15 Oct 2026, his call):
  // before 4am it reads "Thursday, October 15 · Wednesday's night" — the
  // suffix, from Home, names the night; the counts below say "on Wednesday".
  const date = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  const late = today !== new Date().toLocaleDateString('en-CA');
  const when = late ? `on ${new Date(`${today}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' })}` : 'today';

  return (
    <section className={`cond${band ? ` cond-${band}` : ' cond-none'}`} aria-label="How it is right now">
      <div className="row-between">
        <span className="cond-date">{date}{suffix ? ` · ${suffix}` : ''}</span>
        <Link className="btn btn-ghost btn-compact cond-settings" to="/settings">
          Settings
        </Link>
      </div>
      {last ? (
        <>
          <h1 className="cond-word">{moodLabel(last.mood)}</h1>
          <p className="cond-sub">
            {signedFigure(last.mood)} · said at {clock(last.recordedAt)} ·{' '}
            {checkins === 1 ? `one check-in ${when}` : `${checkins} check-ins ${when}`}
          </p>
        </>
      ) : (
        <>
          <h1 className="cond-word cond-word-none">{greeting()}.</h1>
          <p className="cond-sub">{describe}</p>
        </>
      )}
      {/* THE THREADS STRIP: what is running, with his own count. Events (the
          big things) keep the ochre edge they have everywhere; a thread, which
          is allowed to be ordinary, gets a plain one. Release 2 of 2.0. */}
      <ThreadChips events={events} day={today}>
        {onThreadStarted && (
          <li>
            <button type="button" className="thr thr-add" onClick={() => setStarting(true)} aria-expanded={starting}>
              + thread
            </button>
          </li>
        )}
        {peopleCount > 0 && (
          <li>
            <Link className="thr thr-people" to="/people">
              People <span className="thr-n">{peopleCount}</span>
            </Link>
          </li>
        )}
      </ThreadChips>
      {starting && onThreadStarted && (
        <StartThread
          onCreated={(t) => {
            setStarting(false);
            onThreadStarted(t);
          }}
          onCancel={() => setStarting(false)}
        />
      )}
    </section>
  );
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  if (hour < 22) return 'Evening';
  return 'Late one';
}

/**
 * "since 17 Aug · day 52" — his own count, from the date he gave it. When a
 * thread counts from a date other than its start the line says only the count,
 * because "since" would name the wrong date.
 */
